import type { GeoCountry } from "@hobbyshopify/backend-client";
import type { FormEvent } from "react";
import type { LoadStatus } from "../core/primitives/load-status";
import type { LocationDraft, LocationErrors, LocationField } from "../core/primitives/location-form";
import type { GeoRegionList } from "../core/repositories/countries-repository";

export interface LocationFieldsProps {
  draft: LocationDraft;
  errors: LocationErrors;
  countries: readonly GeoCountry[];
  countriesStatus: LoadStatus;
  /** The draft's country's regions; empty where the country has none. */
  regions: GeoRegionList;
  onChange: (field: LocationField, value: string) => void;
  idPrefix: string;
  /** A venue for a show is a building with a name and an address: no phone, no hours. */
  kind?: "branch" | "venue";
}

/**
 * The address fields themselves, shared by the onboarding step and the settings
 * screen so the two can never drift into asking for different things.
 */
export function LocationFields({
  draft,
  errors,
  countries,
  countriesStatus,
  regions,
  onChange,
  idPrefix,
  kind = "branch",
}: LocationFieldsProps) {
  const labelCls = "mb-1.5 block text-xs font-medium text-white";
  const pending = countriesStatus.kind === "idle" || countriesStatus.kind === "loading";
  const branch = kind === "branch";

  return (
    <>
      <div className="flex gap-2.5">
        <div className="flex-1">
          <label htmlFor={`${idPrefix}-name`} className={labelCls}>
            {branch ? "Location name" : "Venue name"}
          </label>
          <input
            id={`${idPrefix}-name`}
            value={draft.name}
            onChange={(e) => onChange("name", e.target.value)}
            className="field"
            placeholder={branch ? "e.g. Uptown" : "e.g. Elks Lodge"}
          />
          {errors.name && <p className="field-error">{errors.name}</p>}
        </div>
        {branch && (
          <div className="flex-1">
            <label htmlFor={`${idPrefix}-phone`} className={labelCls}>
              Phone <span className="font-normal text-faint">Optional</span>
            </label>
            <input
              id={`${idPrefix}-phone`}
              value={draft.phone}
              onChange={(e) => onChange("phone", e.target.value)}
              className="field"
              placeholder="Optional"
            />
          </div>
        )}
      </div>

      {/* Country first, not last as it was: it decides whether a state is asked
          for and how the postal code is checked, so asking for it after those
          fields makes the form rearrange itself under the merchant. */}
      <div>
        <label htmlFor={`${idPrefix}-country`} className={labelCls}>
          Country
        </label>
        <select
          id={`${idPrefix}-country`}
          value={draft.countryCode}
          onChange={(e) => onChange("countryCode", e.target.value)}
          className="field"
          disabled={pending}
        >
          <option value="">{pending ? "Loading countries…" : "Select a country"}</option>
          {countries.map((country) => (
            <option key={country.code} value={country.code}>
              {country.name}
            </option>
          ))}
        </select>
        {errors.countryCode && <p className="field-error">{errors.countryCode}</p>}
        {countriesStatus.kind === "error" && (
          <p className="field-error">Couldn't load the country list. Reload to try again.</p>
        )}
      </div>

      <div>
        <label htmlFor={`${idPrefix}-address1`} className={labelCls}>
          Street address
        </label>
        <input
          id={`${idPrefix}-address1`}
          value={draft.address1}
          onChange={(e) => onChange("address1", e.target.value)}
          className="field"
          placeholder="Address line 1"
        />
        {errors.address1 && <p className="field-error">{errors.address1}</p>}
      </div>

      <input
        value={draft.address2}
        onChange={(e) => onChange("address2", e.target.value)}
        className="field"
        placeholder="Address line 2 (optional)"
        aria-label="Address line 2"
      />

      <div className="flex gap-2.5">
        <div className="flex-[1.4]">
          <input
            value={draft.city}
            onChange={(e) => onChange("city", e.target.value)}
            className="field w-full"
            placeholder="City"
            aria-label="City"
          />
        </div>
        {/* A select where the country has a fixed list, absent where it does not
            — a free-text state box for a country with no states is a box nobody
            can fill in correctly. */}
        {regions.length > 0 && (
          <div className="flex-1">
            <select
              value={draft.province}
              onChange={(e) => onChange("province", e.target.value)}
              className="field w-full"
              aria-label="State or province"
            >
              <option value="">State</option>
              {regions.map((region) => (
                <option key={region.code} value={region.code}>
                  {region.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="w-[86px]">
          <input
            value={draft.zip}
            onChange={(e) => onChange("zip", e.target.value)}
            className="field w-full"
            placeholder="ZIP"
            aria-label="Postal code"
          />
        </div>
      </div>
      {(errors.city || errors.province || errors.zip) && (
        <p className="field-error">{errors.city ?? errors.province ?? errors.zip}</p>
      )}

      {/* Per branch, not per shop: a merchant with two shops rarely keeps the
          same hours at both, and the public shop page lists each one with its
          own. Free text — a structured schedule is a bigger ask than a small
          shop wants to fill in. */}
      {branch && (
        <div>
          <label htmlFor={`${idPrefix}-openingHours`} className={labelCls}>
            Opening hours <span className="font-normal text-faint">Optional</span>
          </label>
          <input
            id={`${idPrefix}-openingHours`}
            value={draft.openingHours}
            onChange={(e) => onChange("openingHours", e.target.value)}
            className="field"
            placeholder="Mon–Sat 10–6, Sun closed"
          />
        </div>
      )}
    </>
  );
}

/** The add form and the edit form are the same fields; only the verbs differ. */
export function LocationEditor({
  submitLabel,
  submittingLabel,
  busy,
  onSubmit,
  onCancel,
  ...fields
}: LocationFieldsProps & {
  submitLabel: string;
  submittingLabel: string;
  busy: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit();
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-2.5 rounded-row border border-line p-3.5">
      <LocationFields {...fields} />

      <div className="mt-0.5 flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="btn-secondary btn-sm">
          Cancel
        </button>
        <button type="submit" disabled={busy} className="btn-primary btn-sm">
          {busy ? submittingLabel : submitLabel}
        </button>
      </div>
    </form>
  );
}
